import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Container,
  Divider,
  Heading,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { Elements } from '@stripe/react-stripe-js';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiCreditCard, FiDollarSign, FiMoreVertical, FiAlertCircle, FiPlus, FiTrash2, FiExternalLink, FiCheckCircle, FiSend, FiArrowDownLeft, FiArrowUpRight } from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { useMyPayments } from '../../hooks/usePayments';
import { useMyPayoutRequests } from '../../hooks/usePayouts';
import {
  useStripeConfig,
  callAddPaymentMethod,
  callRemovePaymentMethod,
  callCreateStripeConnectAccount,
  callGetStripeConnectStatus,
  callRequestPayout,
  getPaymentErrorMessage,
} from '../../hooks/useStripe';
import CardForm from './CardForm';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { EmptyState } from '../common/EmptyState';
import { StatusBadge } from '../common/StatusBadge';
import { formatRelativeTime } from '../../utils/formatters';
import { Price } from '../common/Price';

// ─── Shared helpers ──────────────────────────────────────────────────────────

function brandLabel(brand) {
  const brands = {
    visa: 'Visa',
    mastercard: 'Mastercard',
    amex: 'American Express',
    discover: 'Discover',
    diners: 'Diners Club',
    jcb: 'JCB',
    unionpay: 'UnionPay',
  };
  return brands[brand] || (brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : 'Card');
}

function getTimestamp(item) {
  const ts = item.createdAt || item.submittedAt;
  if (!ts) return 0;
  if (ts.seconds) return ts.seconds;
  if (ts.toDate) return ts.toDate().getTime() / 1000;
  return 0;
}

// ─── Transaction row (used in both tabs) ─────────────────────────────────────

function TransactionRow({ item, type }) {
  const navigate = useNavigate();

  if (type === 'payout') {
    return (
      <HStack py={3} justify="space-between">
        <HStack spacing={3}>
          <Icon as={FiArrowUpRight} color="blue.500" boxSize={4} />
          <Box>
            <HStack spacing={2}>
              <Text fontSize="sm" fontWeight="medium">Payout</Text>
              <StatusBadge status={item.status} />
            </HStack>
            {item.rejectionReason && (
              <Text fontSize="xs" color="red.500" noOfLines={1}>{item.rejectionReason}</Text>
            )}
          </Box>
        </HStack>
        <HStack spacing={3}>
          <Text fontSize="sm" fontWeight="600" color="blue.600">-<Price cents={item.amount} size="sm" /></Text>
          <Text fontSize="xs" color="gray.400" minW="60px" textAlign="right">
            {item.createdAt ? formatRelativeTime(item.createdAt) : ''}
          </Text>
        </HStack>
      </HStack>
    );
  }

  const isIncoming = type === 'incoming';
  const counterparty = isIncoming ? item.collectorUsername : item.claimantUsername;

  return (
    <HStack py={3} justify="space-between">
      <HStack spacing={3} flex={1} minW={0}>
        <Icon
          as={isIncoming ? FiArrowDownLeft : FiArrowUpRight}
          color={isIncoming ? 'green.500' : 'gray.500'}
          boxSize={4}
          flexShrink={0}
        />
        <Box minW={0} flex={1}>
          <Text
            as={Link}
            to={`/submissions/${item.claimId}`}
            fontSize="sm"
            fontWeight="medium"
            noOfLines={1}
            _hover={{ color: 'orange.500' }}
          >
            {item.bountyTitle}
          </Text>
          <Text fontSize="xs" color="gray.500" noOfLines={1}>
            {isIncoming ? 'From' : 'To'} {counterparty}
          </Text>
        </Box>
      </HStack>
      <HStack spacing={3} flexShrink={0}>
        <Text fontSize="sm" fontWeight="600" color={isIncoming ? 'green.600' : 'gray.700'}>
          {isIncoming ? '+' : '-'}<Price cents={item.amount} size="sm" />
        </Text>
        <Text fontSize="xs" color="gray.400" minW="60px" textAlign="right">
          {item.createdAt ? formatRelativeTime(item.createdAt) : ''}
        </Text>
      </HStack>
      <Menu>
        <MenuButton
          as={IconButton}
          icon={<Icon as={FiMoreVertical} />}
          variant="ghost"
          size="xs"
          aria-label="Options"
        />
        <MenuList minW="180px">
          <MenuItem
            icon={<Icon as={FiAlertCircle} />}
            onClick={() => navigate(`/support/create?subject=${encodeURIComponent(`Payment Issue: ${item.id}`)}`)}
          >
            Report an issue
          </MenuItem>
        </MenuList>
      </Menu>
    </HStack>
  );
}

// ─── Payment Methods ─────────────────────────────────────────────────────────

function PaymentMethods({ userData }) {
  const { stripePromise, loading: stripeLoading } = useStripeConfig();
  const toast = useToast();
  const removeDialog = useDisclosure();
  const cancelRef = useRef();
  const getPaymentMethodRef = useRef(null);

  const [showAddCard, setShowAddCard] = useState(false);
  const [adding, setAdding] = useState(false);
  const [removingId, setRemovingId] = useState(null);
  const [cardToRemove, setCardToRemove] = useState(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  const savedCards = userData?.stripePaymentMethods || [];
  const isFull = savedCards.length >= 3;

  const handleCardFormReady = useCallback((fn) => { getPaymentMethodRef.current = fn; }, []);

  const handleAddCard = async () => {
    if (!getPaymentMethodRef.current) return;
    setAdding(true);
    try {
      const paymentMethodId = await getPaymentMethodRef.current();
      await callAddPaymentMethod(paymentMethodId);
      toast({ title: 'Card added successfully', status: 'success', duration: 3000, isClosable: true });
      setShowAddCard(false);
    } catch (err) {
      toast({ title: 'Failed to add card', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setAdding(false);
    }
  };

  const handleRemoveCard = async () => {
    if (!cardToRemove) return;
    setRemovingId(cardToRemove.id);
    try {
      await callRemovePaymentMethod(cardToRemove.id);
      toast({ title: 'Card removed', status: 'success', duration: 3000, isClosable: true });
      removeDialog.onClose();
      setCardToRemove(null);
    } catch (err) {
      toast({ title: 'Failed to remove card', description: err.message, status: 'error', duration: 5000, isClosable: true });
    } finally {
      setRemovingId(null);
    }
  };

  const openRemoveDialog = (card) => {
    setCardToRemove(card);
    removeDialog.onOpen();
  };

  return (
    <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
      <HStack px={5} py={4} justify="space-between">
        <HStack spacing={3}>
          <Icon as={FiCreditCard} color="gray.500" />
          <Heading size="sm">Payment Methods</Heading>
        </HStack>
        {isFull ? (
          <Text fontSize="xs" color="gray.400">{savedCards.length}/3 saved</Text>
        ) : !showAddCard && (
          <Button
            size="xs"
            variant="outline"
            leftIcon={<Icon as={FiPlus} boxSize={3} />}
            onClick={() => setShowAddCard(true)}
          >
            Add Card
          </Button>
        )}
      </HStack>

      <Divider />

      <VStack align="stretch" spacing={0} divider={<Divider />}>
        {savedCards.map((card) => (
          <HStack key={card.id} px={5} py={4} justify="space-between">
            <HStack spacing={3}>
              <Box p={2} bg="blue.50" rounded="md">
                <Icon as={FiCreditCard} color="blue.500" boxSize={5} />
              </Box>
              <Box>
                <Text fontSize="sm" fontWeight="medium">
                  {brandLabel(card.brand)} ending in {card.last4}
                </Text>
                <Text fontSize="xs" color="gray.500">Used for task payments</Text>
              </Box>
            </HStack>
            <IconButton
              icon={<Icon as={FiTrash2} />}
              variant="ghost"
              size="sm"
              colorScheme="red"
              aria-label="Remove card"
              onClick={() => openRemoveDialog(card)}
            />
          </HStack>
        ))}

        {savedCards.length === 0 && !showAddCard && (
          <Box px={5} py={6} textAlign="center">
            <Text fontSize="sm" color="gray.500" mb={3}>No payment method on file</Text>
            <Button
              size="sm"
              colorScheme="orange"
              leftIcon={<Icon as={FiPlus} boxSize={3.5} />}
              onClick={() => setShowAddCard(true)}
            >
              Add Card
            </Button>
          </Box>
        )}

        {showAddCard && (
          <Box px={5} py={4}>
            <Text fontSize="sm" fontWeight="medium" mb={3}>Add a debit or credit card</Text>
            {stripeLoading || !stripePromise ? (
              <HStack spacing={2} py={3}>
                <Spinner size="sm" />
                <Text fontSize="sm" color="gray.500">Loading...</Text>
              </HStack>
            ) : (
              <Elements stripe={stripePromise}>
                <VStack align="stretch" spacing={3}>
                  <CardForm onReady={handleCardFormReady} />
                  <HStack spacing={2}>
                    <Button size="sm" colorScheme="orange" onClick={handleAddCard} isLoading={adding} loadingText="Adding...">
                      Add Card
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setShowAddCard(false)} isDisabled={adding}>
                      Cancel
                    </Button>
                  </HStack>
                </VStack>
              </Elements>
            )}
          </Box>
        )}
      </VStack>

      <AlertDialog isOpen={removeDialog.isOpen} leastDestructiveRef={cancelRef} onClose={removeDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Remove Card</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to remove your {brandLabel(cardToRemove?.brand)} ending in {cardToRemove?.last4}?
              {savedCards.length === 1 && ' You\'ll need to add a new card before posting any tasks.'}
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={removeDialog.onClose}>Cancel</Button>
              <Button colorScheme="red" onClick={handleRemoveCard} isLoading={!!removingId} ml={3}>
                Remove
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}

// ─── Spending Tab ────────────────────────────────────────────────────────────

function SpendingTab({ userData, outgoing }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  return (
    <VStack spacing={5} align="stretch">
      <PaymentMethods userData={userData} />

      <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
        <Box px={5} py={4}>
          <Heading size="sm">Transaction History</Heading>
        </Box>
        <Divider />
        {outgoing.length === 0 ? (
          <Box px={5} py={8} textAlign="center">
            <Text fontSize="sm" color="gray.400">No payments yet</Text>
          </Box>
        ) : (
          <VStack align="stretch" spacing={0} divider={<Divider />} px={5}>
            {outgoing.map((p) => (
              <TransactionRow key={p.id} item={p} type="outgoing" />
            ))}
          </VStack>
        )}
      </Box>
    </VStack>
  );
}

// ─── Earnings Tab ────────────────────────────────────────────────────────────

function EarningsTab({ userData, incoming }) {
  const toast = useToast();
  const payoutModal = useDisclosure();
  const { currentUser } = useAuth();
  const { requests: payoutRequests } = useMyPayoutRequests(currentUser?.uid);

  const hasConnectAccount = !!userData?.stripeConnectAccountId;
  const [connectStatus, setConnectStatus] = useState(
    hasConnectAccount
      ? { hasAccount: true, onboardingComplete: true, readyToReceivePayments: true }
      : { hasAccount: false, onboardingComplete: false, readyToReceivePayments: false }
  );
  const [settingUp, setSettingUp] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState('');
  const [requesting, setRequesting] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.100', 'gray.700');

  // Compute balance client-side from already-fetched data (instant, no Cloud Function)
  const balance = useMemo(() => {
    const VALID_STATUSES = ['succeeded', 'manually_verified', 'admin_bypass'];
    const totalEarned = incoming.reduce((sum, tx) =>
      VALID_STATUSES.includes(tx.paymentStatus) ? sum + (tx.amount || 0) : sum, 0);
    const totalPending = payoutRequests.reduce((sum, r) =>
      (r.status === 'pending' || r.status === 'approved') ? sum + (r.amount || 0) : sum, 0);
    const totalPaidOut = payoutRequests.reduce((sum, r) =>
      r.status === 'succeeded' ? sum + (r.amount || 0) : sum, 0);
    return {
      totalEarned,
      totalPending,
      totalPaidOut,
      availableBalance: Math.max(0, totalEarned - totalPending - totalPaidOut),
    };
  }, [incoming, payoutRequests]);

  const refreshStatus = useCallback(async () => {
    try {
      const result = await callGetStripeConnectStatus();
      setConnectStatus(result);
    } catch (err) {
      console.error('Failed to get connect status:', err);
    }
  }, []);

  useEffect(() => {
    if (hasConnectAccount) refreshStatus();
  }, [hasConnectAccount, refreshStatus]);

  const handleSetup = async () => {
    setSettingUp(true);
    try {
      const result = await callCreateStripeConnectAccount();
      if (result.url) window.location.href = result.url;
    } catch (err) {
      toast({ title: 'Setup failed', description: getPaymentErrorMessage(err), status: 'error', duration: 5000, isClosable: true });
      setSettingUp(false);
    }
  };

  const handleRequestPayout = async () => {
    const cents = Math.round(parseFloat(payoutAmount) * 100);
    if (isNaN(cents) || cents < 100) {
      toast({ title: 'Minimum payout is $1.00', status: 'warning', duration: 3000, isClosable: true });
      return;
    }
    setRequesting(true);
    try {
      await callRequestPayout(cents);
      toast({ title: 'Payout requested', description: 'Your request will be reviewed by an admin.', status: 'success', duration: 5000, isClosable: true });
      payoutModal.onClose();
      setPayoutAmount('');
    } catch (err) {
      toast({ title: 'Request failed', description: getPaymentErrorMessage(err), status: 'error', duration: 5000, isClosable: true });
    } finally {
      setRequesting(false);
    }
  };

  // Merge incoming payments and payout requests into a single timeline
  const timeline = useMemo(() => {
    const items = [];
    incoming.forEach((p) => items.push({ ...p, _type: 'incoming' }));
    payoutRequests.forEach((r) => items.push({ ...r, _type: 'payout' }));
    items.sort((a, b) => getTimestamp(b) - getTimestamp(a));
    return items;
  }, [incoming, payoutRequests]);

  const isOnboarded = connectStatus?.hasAccount && connectStatus?.onboardingComplete && connectStatus?.readyToReceivePayments;
  const available = balance.availableBalance;

  // Payout setup card
  const renderPayoutSetup = () => {
    if (!connectStatus?.hasAccount) {
      return (
        <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} p={5}>
          <HStack justify="space-between" align="center">
            <Box>
              <HStack spacing={2} mb={1}>
                <Icon as={FiSend} color="gray.500" />
                <Text fontWeight="semibold" fontSize="sm">Receive Payments</Text>
              </HStack>
              <Text fontSize="xs" color="gray.500">Set up your payout account to withdraw earnings</Text>
            </Box>
            <Button
              colorScheme="orange"
              size="sm"
              leftIcon={<Icon as={FiExternalLink} boxSize={3.5} />}
              onClick={handleSetup}
              isLoading={settingUp}
              loadingText="Setting up..."
            >
              Set Up Payouts
            </Button>
          </HStack>
        </Box>
      );
    }

    if (!isOnboarded) {
      return (
        <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} p={5}>
          <HStack justify="space-between" align="center">
            <Box>
              <HStack spacing={2} mb={1}>
                <Icon as={FiSend} color="gray.500" />
                <Text fontWeight="semibold" fontSize="sm">Receive Payments</Text>
                <Badge colorScheme="yellow" fontSize="xs">Incomplete</Badge>
              </HStack>
              <Text fontSize="xs" color="gray.500">Finish providing the required information to Stripe</Text>
            </Box>
            <Button
              colorScheme="orange"
              size="sm"
              leftIcon={<Icon as={FiExternalLink} boxSize={3.5} />}
              onClick={handleSetup}
              isLoading={settingUp}
              loadingText="Loading..."
            >
              Complete Setup
            </Button>
          </HStack>
        </Box>
      );
    }

    // Fully onboarded — balance card
    return (
      <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
        <HStack px={5} py={4} justify="space-between">
          <HStack spacing={2}>
            <Icon as={FiCheckCircle} color="green.500" boxSize={4} />
            <Text fontSize="sm" fontWeight="medium" color="green.600">Payouts Ready</Text>
          </HStack>
          <Button
            size="xs"
            variant="outline"
            leftIcon={<Icon as={FiExternalLink} boxSize={3} />}
            as="a"
            href="https://connect.stripe.com/express_login"
            target="_blank"
            rel="noopener noreferrer"
          >
            Manage
          </Button>
        </HStack>
        <Divider />
        <Box px={5} py={4}>
          <HStack justify="space-between" align="flex-end">
            <SimpleGrid columns={3} spacing={6}>
              <Stat size="sm">
                <StatLabel fontSize="xs">Available</StatLabel>
                <StatNumber fontSize="lg" color="green.600"><Price cents={available} /></StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">Total Earned</StatLabel>
                <StatNumber fontSize="lg"><Price cents={balance.totalEarned} /></StatNumber>
              </Stat>
              <Stat size="sm">
                <StatLabel fontSize="xs">Paid Out</StatLabel>
                <StatNumber fontSize="lg"><Price cents={balance.totalPaidOut} /></StatNumber>
              </Stat>
            </SimpleGrid>
            {available >= 100 && (
              <Button
                colorScheme="green"
                size="sm"
                leftIcon={<Icon as={FiDollarSign} boxSize={4} />}
                onClick={payoutModal.onOpen}
                flexShrink={0}
              >
                Request Payout
              </Button>
            )}
          </HStack>
        </Box>
      </Box>
    );
  };

  return (
    <>
      <VStack spacing={5} align="stretch">
        {renderPayoutSetup()}

        <Box bg={cardBg} rounded="xl" borderWidth={1} borderColor={borderColor} overflow="hidden">
          <Box px={5} py={4}>
            <Heading size="sm">Activity</Heading>
          </Box>
          <Divider />
          {timeline.length === 0 ? (
            <Box px={5} py={8} textAlign="center">
              <Text fontSize="sm" color="gray.400">No earnings yet</Text>
            </Box>
          ) : (
            <VStack align="stretch" spacing={0} divider={<Divider />} px={5}>
              {timeline.map((item) => (
                <TransactionRow key={item.id} item={item} type={item._type} />
              ))}
            </VStack>
          )}
        </Box>
      </VStack>

      {/* Payout request modal */}
      <Modal isOpen={payoutModal.isOpen} onClose={payoutModal.onClose} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Request Payout</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="sm" color="gray.600">
                Available balance: <strong><Price cents={available} /></strong>
              </Text>
              <InputGroup>
                <InputLeftElement pointerEvents="none" color="gray.400">$</InputLeftElement>
                <Input
                  type="number"
                  step="0.01"
                  min="1"
                  max={(available / 100).toFixed(2)}
                  placeholder="0.00"
                  value={payoutAmount}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === '' || /^\d*\.?\d{0,2}$/.test(val)) {
                      setPayoutAmount(val);
                    }
                  }}
                />
              </InputGroup>
              <Text fontSize="xs" color="gray.500">
                Minimum $1.00. Payouts are reviewed by our team and typically processed within 1-2 business days.
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={payoutModal.onClose}>Cancel</Button>
            <Button
              colorScheme="green"
              onClick={handleRequestPayout}
              isLoading={requesting}
              isDisabled={!payoutAmount || parseFloat(payoutAmount) < 1 || parseFloat(payoutAmount) * 100 > available}
            >
              Request Payout
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function Payments() {
  const { currentUser } = useAuth();
  const { userData } = useUser();
  const { outgoing, incoming, loading } = useMyPayments(currentUser?.uid);
  const [searchParams] = useSearchParams();
  const toast = useToast();

  const isVerified = userData?.verificationStatus === 'verified';

  // Default to Earnings tab if returning from Stripe Connect or if user is verified
  const [tabIndex, setTabIndex] = useState(() => {
    const stripeConnect = new URLSearchParams(window.location.search).get('stripe_connect');
    return (stripeConnect === 'return' && isVerified) ? 1 : 0;
  });

  useEffect(() => {
    const stripeConnect = searchParams.get('stripe_connect');
    if (stripeConnect === 'return') {
      toast({
        title: 'Payout setup updated',
        description: 'Your payout account information has been saved.',
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
    }
  }, [searchParams, toast]);

  if (loading) return <LoadingSpinner message="Loading payments..." />;

  return (
    <Container maxW="3xl" py={6}>
      <VStack spacing={5} align="stretch">
        <Button
          as={Link}
          to="/tasks"
          variant="ghost"
          leftIcon={<Icon as={FiArrowLeft} />}
          alignSelf="flex-start"
          size="sm"
        >
          Back to Tasks
        </Button>

        <Heading size="lg">Payments</Heading>

        {isVerified ? (
          <Tabs
            index={tabIndex}
            onChange={setTabIndex}
            variant="unstyled"
            isLazy
          >
            <TabList borderBottom="1px solid" borderBottomColor="gray.200">
              <Tab
                fontWeight="medium"
                fontSize="sm"
                color="gray.500"
                pb={2}
                mr={4}
                borderBottom="2px solid transparent"
                mb="-1px"
                _selected={{ color: 'orange.500', borderBottomColor: 'orange.500' }}
              >Payments</Tab>
              <Tab
                fontWeight="medium"
                fontSize="sm"
                color="gray.500"
                pb={2}
                mr={4}
                borderBottom="2px solid transparent"
                mb="-1px"
                _selected={{ color: 'orange.500', borderBottomColor: 'orange.500' }}
              >Earnings</Tab>
            </TabList>
            <TabPanels>
              <TabPanel px={0} pt={5}>
                <SpendingTab userData={userData} outgoing={outgoing} />
              </TabPanel>
              <TabPanel px={0} pt={5}>
                <EarningsTab userData={userData} incoming={incoming} />
              </TabPanel>
            </TabPanels>
          </Tabs>
        ) : (
          <SpendingTab userData={userData} outgoing={outgoing} />
        )}
      </VStack>
    </Container>
  );
}
